import type { Metadata, MetadataRoute } from "next";
import { SITE_NAME, siteUrl } from "@/lib/brand";
import { getTool, TOOLS, type Tool } from "@/lib/tools";

export const HOME_TITLE = `${SITE_NAME}: Free PDF & Image Tools Online, No Upload`;
export const HOME_DESCRIPTION =
  "Merge, split, edit, sign and convert PDFs. Resize, compress, crop and convert images. Free, no sign-up, and your files never leave your browser.";

/** Title, description, canonical address and share preview for one page.
 *  The layout adds " | PDF & Pixel" to the title unless `absoluteTitle`. */
/** The share picture (app/opengraph-image.png). Pages that set their own
 *  openGraph replace the file-based one, so every page lists it again. */
const SHARE_IMAGE = {
  url: "/opengraph-image.png",
  width: 1200,
  height: 630,
  alt: `${SITE_NAME}: free PDF and image tools that run right in your browser`,
};

export function pageMetadata({
  title,
  description,
  path,
  absoluteTitle = false,
}: {
  title: string;
  description: string;
  path: string;
  absoluteTitle?: boolean;
}): Metadata {
  const shareTitle = absoluteTitle ? title : `${title} | ${SITE_NAME}`;
  // Pages live at /<slug>/ (trailing slash); point search engines straight there.
  path = path.endsWith("/") ? path : `${path}/`;
  return {
    title: absoluteTitle ? { absolute: title } : title,
    description,
    alternates: { canonical: path },
    openGraph: {
      type: "website",
      siteName: SITE_NAME,
      locale: "en_US",
      url: path,
      title: shareTitle,
      description,
      images: [SHARE_IMAGE],
    },
    twitter: {
      card: "summary_large_image",
      title: shareTitle,
      description,
      images: [SHARE_IMAGE],
    },
  };
}

/** Title and description for a tool page, from lib/tools.ts (FR-7, NFR-10). */
export function toolMetadata(slug: string): Metadata {
  const tool = getTool(slug);
  if (!tool) return {};
  return pageMetadata({
    title: tool.seoTitle,
    description: tool.seoDescription,
    path: `/${tool.slug}`,
  });
}

/** Every public page for sitemap.xml: home, each tool, then the info pages. */
export function sitemapEntries(base = siteUrl): MetadataRoute.Sitemap {
  return [
    { url: `${base}/`, changeFrequency: "weekly", priority: 1 },
    ...TOOLS.map((tool) => ({
      url: `${base}/${tool.slug}/`,
      changeFrequency: "monthly" as const,
      priority: 0.8,
    })),
    ...["about", "privacy", "terms"].map((page) => ({
      url: `${base}/${page}/`,
      changeFrequency: "yearly" as const,
      priority: 0.3,
    })),
  ];
}

/** robots.txt: every page may be crawled; points crawlers to the sitemap. */
export function robotsRules(base = siteUrl): MetadataRoute.Robots {
  return {
    rules: { userAgent: "*", allow: "/" },
    sitemap: `${base}/sitemap.xml`,
  };
}

// Structured data (schema.org JSON-LD) helps Google show rich results.

/** The site itself, for the home page. */
export function siteJsonLd(base = siteUrl) {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        "@id": `${base}/#website`,
        name: SITE_NAME,
        url: `${base}/`,
        description: HOME_DESCRIPTION,
      },
      {
        "@type": "Organization",
        "@id": `${base}/#organization`,
        name: SITE_NAME,
        url: `${base}/`,
        logo: `${base}/icon.svg`,
      },
    ],
  };
}

/** Questions and answers shown on the page, as an FAQPage. */
export function faqJsonLd(faq: { q: string; a: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faq.map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };
}

/** A tool page: a free web app, plus its place in the site (breadcrumbs). */
export function toolJsonLd(tool: Tool, base = siteUrl) {
  const url = `${base}/${tool.slug}/`;
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebApplication",
        name: tool.name,
        url,
        description: tool.seoDescription,
        applicationCategory: "UtilitiesApplication",
        operatingSystem: "Any (runs in the browser)",
        browserRequirements: "Requires JavaScript",
        isAccessibleForFree: true,
        offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        publisher: { "@id": `${base}/#organization` },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: SITE_NAME,
            item: `${base}/`,
          },
          { "@type": "ListItem", position: 2, name: tool.name, item: url },
        ],
      },
    ],
  };
}

/** Text for a <script type="application/ld+json">; "<" is escaped so the
 *  data can never close the script tag early. */
export function jsonLdText(data: unknown): string {
  return JSON.stringify(data).replace(/</g, "\\u003c");
}
